import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Box, Paper, Tooltip, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import StarOutlineRounded from '@mui/icons-material/StarOutlineRounded';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import AccessibleOutlinedIcon from '@mui/icons-material/AccessibleOutlined';
import GradingIcon from '@mui/icons-material/Grading';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import PassengerPreferencesDialog from './PassengerPreferencesDialog';
import ProfileRow from './ProfileRow';
import {
  DEFAULT_SERVICE_LEVEL,
  PassengerPreference,
  PassengerPreferenceBC,
} from '../../../data/models/passenger.model';

const PREFIX = 'PassengerPreferences';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    gap: theme.spacing(4),
  },

  [`& .${classes.details}`]: {
    padding: theme.spacing(3, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingBottom: theme.spacing(1),
  },
}));

interface PassengerPreferencesProps {
  passengerUUID: string;
  passengerPreferences: PassengerPreference[];
}

export default function PassengerPreferences({
  passengerUUID,
  passengerPreferences,
}: PassengerPreferencesProps) {
  const t = useCommonTranslation();
  const [preferenceDialogOpen, setPreferenceDialogOpen] = React.useState(false);

  const editPreference = () => {
    setPreferenceDialogOpen(true);
  };

  const getPreferenceValue = (attrBC: string, dflt = '') => {
    const pref = passengerPreferences.find(
      (pp) => pp.passengerProfileAttributeTypeBC === attrBC,
    );
    return pref?.attributeValue || dflt;
  };

  return (
    <StyledBox
      className={classes.container}
      data-testid="passenger-preferences-container">
      <Paper
        elevation={2}
        className={classes.details}
        data-testid="passenger-preferences-section">
        <Box
          className={classes.header}
          data-testid="passenger-preferences-header">
          <Typography variant="h3">{t('passenger.preferences')}</Typography>
          <Tooltip title={t('passenger.editPreferences')}>
            <span>
              <IconButton
                data-testid="delete-address"
                data-fs-element="EditPreferencesIconButton"
                onClick={(evt) => editPreference()}
                color="primary">
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <ProfileRow
          label={t('passenger.communication')}
          value={getPreferenceValue(PassengerPreferenceBC.communication)}
          icon={PeopleAltIcon}
          testidPrefix="passenger-preferences"
        />
        <ProfileRow
          label={t('passenger.language')}
          value={getPreferenceValue(PassengerPreferenceBC.language)}
          icon={GradingIcon}
          testidPrefix="passenger-preferences"
        />
        <ProfileRow
          label={t('passenger.provider')}
          value={getPreferenceValue(PassengerPreferenceBC.provider)}
          icon={DirectionsCarFilledOutlinedIcon}
          testidPrefix="passenger-preferences"
        />
        <ProfileRow
          label={t('passenger.serviceLevel')}
          value={getPreferenceValue(
            PassengerPreferenceBC.serviceLevel,
            DEFAULT_SERVICE_LEVEL,
          )}
          icon={AccessibleOutlinedIcon}
          testidPrefix="passenger-preferences"
        />
        <ProfileRow
          label={t('passenger.vip')}
          value={getPreferenceValue(PassengerPreferenceBC.vip)}
          icon={StarOutlineRounded}
          testidPrefix="passenger-preferences"
        />
      </Paper>
      <PassengerPreferencesDialog
        passengerUUID={passengerUUID}
        passengerPreferences={passengerPreferences}
        open={preferenceDialogOpen}
        close={() => setPreferenceDialogOpen(false)}
      />
    </StyledBox>
  );
}
